import { useMemo } from 'react';

import { AccessLevelIcon } from '@dbt-labs/dbt-dag';
import { CellContext, ColumnDef, Icon, Link, RyeconLineage } from '@dbt-labs/sourdough';

import { AccessLevel, ModelQueryHistoryState } from '@/__generated__/graphql';
import { CustomCell } from '@/components/Table/CustomCell';
import { OwnerHeader } from '@/components/Table/OwnerHeader';
import { TwoLineHeader } from '@/components/Table/TableHeader';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlags } from '@/context/FeatureFlagProvider';
import {
  ModelAppliedListNode,
  ModelWithQueryCount,
} from '@/hooks/discovery/appliedModels';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useHasEnterpriseFeatures } from '@/hooks/useHasEnterpriseFeatures';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { isDevelopment } from '@/typings/url';
import { modelQueryHistorySupported } from '@/util/dbt/adapter';
import { targetResourceSelector } from '@/util/selectorGenerators';
import { toTitleCase } from '@/util/string';
import QueryHistoryTableHeader, {
  queryHistoryCellConfig,
} from '../resource/components/QueryHistoryTableHeader';

type MartsColumnsParams = {
  disableGroupsColumn: boolean;
};

export const useMartsColumns = (
  { disableGroupsColumn }: MartsColumnsParams,
  queryHistoryStateHolder?: ModelQueryHistoryState,
  adapterType?: string,
) => {
  const getLineageLink = useLinkToExplorer('projectLineage');
  const { accountId, projectId, environmentType } = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const { hasAccountFeature } = useDbtCloudAccount();
  const { enableModelQueryHistory } = useDbtCloudProject();
  const featureFlags = useFeatureFlags();

  const queryHistoryEnabled =
    enableModelQueryHistory && !isDevelopment(environmentType);
  const queryHistoryAvailableNotEnabled =
    hasAccountFeature('explorerQueryHistory') &&
    !queryHistoryEnabled &&
    modelQueryHistorySupported(adapterType || '', environmentType, featureFlags);

  const columns = useMemo((): ColumnDef<ModelAppliedListNode>[] => {
    return [
      {
        header: () => <TwoLineHeader title="Name" />,
        accessorKey: 'name',
        accessorFn: (data) => ({
          uniqueId: data.uniqueId,
          name: data.name,
        }),
        cell: (cellContext) => {
          const value = cellContext.getValue() as any;
          return (
            <CustomCell tooltip={value.name}>
              <Link
                isInternal
                to={getDetailsPageLink({
                  accountId,
                  environmentType,
                  projectId,
                  dbtUniqueId: value.uniqueId,
                })}
                className="truncate"
              >
                {value.name}
              </Link>
            </CustomCell>
          );
        },
      },
      {
        accessorKey: 'tags',
        accessorFn: (data) => data.tags?.join(', '),
        header: () => <TwoLineHeader title="Tags" />,
        size: 100,
      },
      ...(queryHistoryEnabled || queryHistoryAvailableNotEnabled
        ? [
            {
              accessorKey: 'usageQueryCount',
              accessorFn: (data: ModelAppliedListNode) =>
                ('usageQueryCount' in data
                  ? data.usageQueryCount
                  : undefined
                )?.toLocaleString() ?? '',
              header: () => {
                return (
                  <QueryHistoryTableHeader
                    queryHistoryState={queryHistoryStateHolder?.state}
                    error={queryHistoryStateHolder?.error}
                    errorCategory={queryHistoryStateHolder?.errorCategory}
                    queryHistoryAvailableNotEnabled={queryHistoryAvailableNotEnabled}
                  />
                );
              },
              cell: queryHistoryCellConfig(
                queryHistoryStateHolder?.state,
                queryHistoryAvailableNotEnabled,
              ) as any,
              size: 95,
              enableSorting: false,
              sortDescFirst: true,
              // meta: {
              //   align: 'right',
              // },
            },
          ]
        : []),
      {
        accessorKey: 'group',
        accessorFn: (data) => ({
          packageName: data.packageName,
          group: data.group,
        }),
        header: () => {
          return <OwnerHeader />;
        },
        cell: (cellContext) => {
          const value = cellContext.getValue() as any;
          const group: string | null = value.group ?? null;
          const packageName: string | null = value.packageName ?? null;
          const children =
            !group || !packageName ? null : (
              <Link
                isInternal
                to={getDetailsPageLink({
                  accountId,
                  environmentType,
                  projectId,
                  dbtUniqueId: `group.${packageName}.${group}`,
                })}
              >
                {group}
              </Link>
            );
          return (
            <CustomCell disable={disableGroupsColumn} tooltip={group ?? ''}>
              {children}
            </CustomCell>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'uniqueId',
        accessorFn: (data) => ({
          uniqueId: data.uniqueId,
          fqn: data.fqn,
        }),
        header: () => <TwoLineHeader title="Lineage" />,
        maxSize: 70,
        minSize: 70,
        size: 46,
        cell: (cellContext) => {
          const value = cellContext.getValue() as any;
          const fqn: string[] | null = value.fqn ?? null;
          const uniqueId: string = value.uniqueId;

          if (!fqn) return null;
          return (
            <Link
              className="mx-auto"
              isInternal
              to={getLineageLink({
                dbtUniqueId: uniqueId,
                accountId,
                environmentType,
                projectId,
                selector: targetResourceSelector({
                  fqn,
                  depth: 2,
                }),
              })}
            >
              <Icon ryecon={RyeconLineage} />
            </Link>
          );
        },
      },
    ];
  }, [
    accountId,
    disableGroupsColumn,
    environmentType,
    getDetailsPageLink,
    getLineageLink,
    projectId,
    queryHistoryAvailableNotEnabled,
    queryHistoryEnabled,
    queryHistoryStateHolder?.error,
    queryHistoryStateHolder?.errorCategory,
    queryHistoryStateHolder?.state,
  ]);
  return columns;
};

export const useMartsColumnsNew = ({ disableGroupsColumn }: MartsColumnsParams) => {
  const { getProjectName } = useDbtCloudAccount();
  const { accountId, projectId, environmentType } = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const getProjectLink = useLinkToExplorer('project');
  const getTargetEnvironmentType = useTargetEnvironmentType();

  const columns: ColumnDef<ModelAppliedListNode>[] = useMemo(
    () => [
      {
        header: () => <TwoLineHeader title="Name" />,
        accessorKey: 'name',
        accessorFn: (data) => ({
          uniqueId: data.uniqueId,
          name: data.name,
        }),
        cell: ({
          getValue,
          row: {
            original: { access },
          },
        }) => {
          const value = getValue() as any;
          const accessLevel = access as AccessLevel;
          return (
            <CustomCell tooltip={value.name}>
              <span className="flex items-center gap-2">
                {access && (
                  <AccessLevelIcon
                    size="sm"
                    access={accessLevel}
                    tooltipConfig={{ placement: 'bottom' }}
                    iconClassName="h-4 w-4 text-fgMain"
                  />
                )}
                <Link
                  isInternal
                  to={getDetailsPageLink({
                    accountId,
                    environmentType,
                    projectId,
                    dbtUniqueId: value.uniqueId,
                  })}
                  className="truncate"
                >
                  {value.name}
                </Link>
              </span>
            </CustomCell>
          );
        },
      },
      {
        header: 'Defined in',
        accessorKey: 'projectId',
        id: 'projectId',
        cell: ({
          getValue,
          row: {
            original: { projectId, environmentId },
          },
        }: CellContext<ModelWithQueryCount, any>) => (
          <Link
            isInternal
            to={getProjectLink({
              accountId: accountId ?? 0,
              projectId: projectId,
              environmentType: getTargetEnvironmentType(environmentId),
            })}
          >
            {getProjectName(getValue())}
          </Link>
        ),
      },
      {
        header: () => <TwoLineHeader title="Modeling layer" />,
        accessorKey: 'modelingLayer',
        accessorFn: (data) => toTitleCase(data.modelingLayer ?? ''),
        size: 100,
      },
      {
        accessorKey: 'tags',
        accessorFn: (data) => data.tags?.join(', '),
        header: () => <TwoLineHeader title="Tags" />,
        size: 100,
      },
      {
        accessorKey: 'group',
        accessorFn: (data) => ({
          packageName: data.packageName,
          group: data.group,
        }),
        header: () => {
          return <OwnerHeader />;
        },
        cell: (cellContext) => {
          const value = cellContext.getValue() as any;
          const group: string | null = value.group ?? null;
          const packageName: string | null = value.packageName ?? null;
          const children =
            !group || !packageName ? null : (
              <Link
                isInternal
                to={getDetailsPageLink({
                  accountId,
                  environmentType,
                  projectId,
                  dbtUniqueId: `group.${packageName}.${group}`,
                })}
              >
                {group}
              </Link>
            );
          return (
            <CustomCell disable={disableGroupsColumn} tooltip={group ?? ''}>
              {children}
            </CustomCell>
          );
        },
        size: 100,
      },
    ],
    [
      getDetailsPageLink,
      accountId,
      environmentType,
      projectId,
      getProjectLink,
      getTargetEnvironmentType,
      getProjectName,
      disableGroupsColumn,
    ],
  );
  return columns;
};

useMartsColumns.useHasEnterpriseFeatures = useHasEnterpriseFeatures;
